import React, { memo } from 'react';
import { styled } from '@compiled/react';
import { injectIntl, FormattedMessage } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button';
import Form from '@atlaskit/form';

import { withFlags } from '@confluence/flags/entry-points/withFlags';

import { sharedi18n } from '../sharedi18n';

import { ColorPickerButton } from './ColorPickerButton';
import type { ContentStateFormProps, CreateContentStateFormData } from './types';
import { i18n } from './i18n';
import { ContentStateTextField } from './ContentStateTextField';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextFieldWrapper = styled.div({
	display: 'inline-block',
	width: '199px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ColorPickerButtonWrapper = styled.div({
	marginRight: token('space.075'),
	display: 'inline-block',
	verticalAlign: 'top',
	marginTop: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FormFooterWrapper = styled.div<{ isVisible: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	display: (props) => (props.isVisible ? 'flex' : 'none'),
	justifyContent: 'flex-end',
	marginTop: token('space.150'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'& > button:first-of-type': {
		marginRight: token('space.100'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ColorAndInputWrapper = styled.div({
	display: 'flex',
});

const ContentStateFormComponent = withFlags(
	({
		onSubmit,
		onCancel,
		showFormFooter,
		selectedColor,
		onSelectColor,
		isColorPickerOpen,
		onColorPickerToggle,
		onColorPickerClose,
		onFocusTextField,
		userOffline,
		colorPickerComponentRef,
		flags,
		intl: { formatMessage },
	}: ContentStateFormProps) => {
		const handleSubmit = (data: CreateContentStateFormData) => {
			const trimmedName = data.name.replace(/\s+/g, ' ').trim();
			if (trimmedName) {
				onSubmit(trimmedName);
			} else {
				// show warning flag if user entered all whitespace
				void flags.showWarningFlag({
					title: <FormattedMessage {...sharedi18n.emptyStatusFlagTitle} />,
					description: <FormattedMessage {...sharedi18n.emptyStatusFlagDescription} />,
				});
			}
		};

		return (
			<Form onSubmit={handleSubmit} testId="content-state-form">
				{({ formProps }) => (
					// eslint-disable-next-line @atlassian/a11y/no-noninteractive-element-interactions
					<form ref={formProps.ref} onKeyDown={formProps.onKeyDown}>
						<ColorAndInputWrapper>
							<ColorPickerButtonWrapper>
								<ColorPickerButton
									onColorPickerToggle={onColorPickerToggle}
									selectedColor={selectedColor}
									onSelectColor={onSelectColor}
									isOpen={isColorPickerOpen}
									onClose={onColorPickerClose}
									userOffline={userOffline}
									colorPickerComponentRef={colorPickerComponentRef}
								/>
							</ColorPickerButtonWrapper>
							<TextFieldWrapper>
								<ContentStateTextField
									onFocusTextField={onFocusTextField}
									showFormFooter={showFormFooter}
									userOffline={userOffline}
								/>
							</TextFieldWrapper>
						</ColorAndInputWrapper>
						<FormFooterWrapper isVisible={showFormFooter} data-testid="content-state-form-footer">
							<Button
								onClick={onCancel}
								spacing="compact"
								appearance="subtle"
								testId="content-state-cancel"
							>
								{formatMessage(i18n.cancel)}
							</Button>
							<Button
								testId="content-state-submit"
								type="submit"
								spacing="compact"
								appearance="primary"
								onClick={formProps.onSubmit}
							>
								{formatMessage(i18n.save)}
							</Button>
						</FormFooterWrapper>
					</form>
				)}
			</Form>
		);
	},
);

export const ContentStateForm = memo(injectIntl(ContentStateFormComponent));
